// Imports
import { Plugin } from '@ckeditor/ckeditor5-core';
import { ViewDocument, ViewDocumentFragment, ViewElement } from '@ckeditor/ckeditor5-engine';


// Plugin
export default class NormalizeTableHeader extends Plugin {
	// Handle plugin initialization
	init() {
		// Initialize
		const editor = this.editor;
		const viewDocument = editor.editing.view.document;
		
		
		// Intercept pasted content before it's inserted into the editor
		editor.plugins.get('ClipboardPipeline').on(
			'inputTransformation',
			(
				_event,
				data: {
					content: ViewDocumentFragment | null;
				}
			) => {
				// Return early
				if (!data.content) {
					return;
				}
				
				
				// Create a new document fragment to hold the normalized content
				const newFragment = new ViewDocumentFragment(viewDocument);
				
				
				// Get children of pasted content
				const children = Array.from(data.content.getChildren());
				
				
				// Iterate over children
				for (const node of children) {
					// Check for relevant element
					if (node.is('element', 'table') || (node.is('element') && node.getChild(0)?.is('element', 'table'))) {
						// Get the table element
						const table = node.is('element', 'table') ? node : node.getChild(0);
						
						
						// Type safety
						if (table && table.is('element', 'table')) {
							// Normalize the table
							const normalizedTable = this._normalizeTable(table, viewDocument);
							
							
							// Add element to new document fragment
							newFragment._appendChild(normalizedTable);
						}
					} else {
						// Add unchanged node
						newFragment._appendChild(node);
					}
				}
				
				
				// Set content to be inserted
				data.content = newFragment;
			}
		);
	}
	
	
	/** Helper function that will normalize table headers to match Markdown table structure */
	private _normalizeTable(table: ViewElement, viewDocument: ViewDocument): ViewElement {
		// Initialize
		const newTable = new ViewElement(viewDocument, 'table');
		const rows: ViewElement[] = [];
		
		
		// Flatten all table rows
		for (const child of table.getChildren()) {
			if (child.is('element', 'thead') || child.is('element', 'tbody')) {
				for (const row of child.getChildren()) {
					if (row.is('element', 'tr')) {
						rows.push(row);
					}
				}
			} else if (child.is('element', 'tr')) {
				rows.push(child);
			}
		}
		
		
		// If there are no rows at all, return the table unchanged
		if (rows.length === 0) {
			return table;
		}
		
		
		// Create header row
		const thead = new ViewElement(viewDocument, 'thead');
		thead._appendChild(rows[0]);
		newTable._appendChild(thead);
		
		
		// Initialize table body
		const tbody = new ViewElement(viewDocument, 'tbody');
		
		
		// Iterate over remaining rows
		for (const row of rows.slice(1)) {
			// Convert any other header rows to body rows
			const convertedRow = this._convertRowToBody(row, viewDocument);
			
			
			// Add row
			tbody._appendChild(convertedRow);
		}
		
		
		// Add body rows to table
		if (tbody.childCount > 0) {
			newTable._appendChild(tbody);
		}
		
		
		// Return updated table
		return newTable;
	}
	
	
	/** Helper function that converts header row to body row */
	private _convertRowToBody(row: ViewElement, viewDocument: ViewDocument): ViewElement {
		// Initialize
		const newRow = new ViewElement(viewDocument, 'tr');
		const cells = Array.from(row.getChildren());
		
		
		// Iterate over cells in row
		for (const cell of cells) {
			// Check if header cell
			if (cell.is('element', 'th')) {
				// Initialize
				const td = new ViewElement(viewDocument, 'td', cell.getAttributes(), cell.getChildren());
				
				
				// Add transformed cell
				newRow._appendChild(td);
			} else {
				// Add unchanged cell
				newRow._appendChild(cell);
			}
		}
		
		
		// Return row
		return newRow;
	}
	
	
	// The name this plugin can be referred to by
	public static get pluginName(): 'NormalizeTableHeader' {
		return 'NormalizeTableHeader';
	}
}
